import React, { useEffect } from 'react';

import { offcanvasHide, offcanvasShow } from '../../utils/offcanvas.util';
import { isCopilotAllowed } from '../../scripts/copilot.auth';
import { isChatAllowed } from '../../scripts/chat.auth';
import { openCopilot, onOpenCopilot } from '../../scripts/copilot.controller';
import { openChat, onOpenChat } from '../../scripts/chat.controller';
import { useFloatingDrawer, DrawerMode } from '../../contexts/FloatingDrawerContext';
import { useChatOptional } from '../../contexts/ChatContext';
import CopilotPanel from './CopilotPanel';
import ChatPanel from './ChatPanel';

interface Props {
  isPushMode: boolean;
}

const OFFCANVAS_IDS: Record<string, { button: string; drawer: string; overlay: string }> = {
  copilot: { button: 'button-copilot', drawer: 'drawer-copilot', overlay: 'overlay-copilot' },
  chat:    { button: 'button-chat',    drawer: 'drawer-chat',    overlay: 'overlay-chat' },
}

function showMode(mode: string) {
  const ids = OFFCANVAS_IDS[mode]
  if (ids) offcanvasShow(ids.button, ids.drawer, ids.overlay)
}

function hideMode(mode: string) {
  const ids = OFFCANVAS_IDS[mode]
  if (ids) offcanvasHide(ids.button, ids.drawer, ids.overlay)
}

function hideAll() {
  Object.keys(OFFCANVAS_IDS).forEach(hideMode)
}

const FloatingDrawer: React.FunctionComponent<Props> = ({isPushMode}) => {
  const {isOpen, activeMode, setOpen, close} = useFloatingDrawer()

  const copilotAllowed = isCopilotAllowed()
  const chatAllowed = isChatAllowed()

  // Read unreadTotal from ChatContext (null if not mounted)
  const chatCtx = useChatOptional()
  const unreadTotal = chatCtx?.unreadTotal ?? 0

  const executeOpen = (mode: DrawerMode) => {
    if (!mode) return
    hideAll()
    setOpen(mode)
    // Chat is always overlay. Copilot uses push when isPushMode is true.
    if (mode === 'chat' || !isPushMode) {
      showMode(mode)
    }
  }

  const executeClose = () => {
    close()
    hideAll()
  }

  useEffect(() => {
    if (isOpen && activeMode) {
      // Chat is always overlay — never push
      if (activeMode === 'chat') {
        showMode(activeMode)
      } else if (isPushMode) {
        hideMode(activeMode)
      } else {
        showMode(activeMode)
      }
    }
  }, [isPushMode, isOpen, activeMode])

  useEffect(() => {
    if (!copilotAllowed) return
    return onOpenCopilot(() => executeOpen('copilot'))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPushMode, copilotAllowed])

  useEffect(() => {
    if (!chatAllowed) return
    return onOpenChat(() => executeOpen('chat'))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPushMode, chatAllowed])

  if (!copilotAllowed && !chatAllowed) return null

  const fabAction = () => {
    if (copilotAllowed) {
      openCopilot({})
    }
    // Chat is triggered from the sidebar footer, not from the FAB
  }

  return (
    <>
      {/* FAB — copilot only, hidden in push mode when open */}
      {copilotAllowed && !(isPushMode && isOpen) && (
        <button
          id="button-copilot"
          className="btn btn-sm btn-primary shadow-sm position-fixed zindex-2 bottom-0 end-0 mb-10 me-10 rounded-circle d-flex align-items-center justify-content-center"
          style={{width: '48px', height: '48px'}}
          type="button"
          onClick={fabAction}
        >
          <span className="svg-icon svg-icon-1 m-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path opacity="0.3" d="M20 3H4C2.89543 3 2 3.89543 2 5V16C2 17.1046 2.89543 18 4 18H4.5C5.05228 18 5.5 18.4477 5.5 19V21.5052C5.5 22.1441 6.21232 22.5253 6.74118 22.1708L11.3753 19.0955C11.5301 18.9912 11.7123 18.9354 11.8989 18.9354H20C21.1046 18.9354 22 18.0399 22 16.9354V5C22 3.89543 21.1046 3 20 3Z" fill="currentColor"/>
              <rect x="6" y="12" width="7" height="2" rx="1" fill="currentColor"/>
              <rect x="6" y="7" width="12" height="2" rx="1" fill="currentColor"/>
            </svg>
          </span>
        </button>
      )}

      {/* Mode switcher tabs — visible when drawer is open and both modes are allowed */}
      {isOpen && copilotAllowed && chatAllowed && (
        <ModeSwitcher activeMode={activeMode} onSwitch={executeOpen} isPushMode={isPushMode} unreadTotal={unreadTotal} />
      )}

      {/* Panels mounted ONCE each */}
      {copilotAllowed && (
        <CopilotPanel
          isOpen={isOpen && activeMode === 'copilot'}
          isPushMode={isPushMode}
          onClose={executeClose}
        />
      )}
      {chatAllowed && (
        <ChatPanel
          isOpen={isOpen && activeMode === 'chat'}
          isPushMode={false}
          onClose={executeClose}
        />
      )}
    </>
  )
}

// --- Mode Switcher (tabs at bottom of drawer) ---

interface ModeSwitcherProps {
  activeMode: DrawerMode;
  onSwitch: (mode: DrawerMode) => void;
  isPushMode: boolean;
  unreadTotal: number;
}

const ModeSwitcher: React.FC<ModeSwitcherProps> = ({activeMode, onSwitch, isPushMode, unreadTotal}) => {
  const style: React.CSSProperties = isPushMode
    ? { position: 'fixed', bottom: '20px', right: '412px', zIndex: 10 }
    : { position: 'fixed', bottom: '20px', right: '412px', zIndex: 1060 }

  return (
    <div className="d-flex flex-column gap-1" style={style}>
      <button
        className={`btn btn-sm btn-icon rounded-circle shadow-sm ${activeMode === 'copilot' ? 'btn-primary' : 'btn-light'}`}
        type="button"
        title="Copilot"
        onClick={() => onSwitch('copilot')}
        style={{width: '36px', height: '36px'}}
      >
        <span className="svg-icon svg-icon-4 m-0">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path opacity="0.3" d="M20 3H4C2.89543 3 2 3.89543 2 5V16C2 17.1046 2.89543 18 4 18H4.5C5.05228 18 5.5 18.4477 5.5 19V21.5052C5.5 22.1441 6.21232 22.5253 6.74118 22.1708L11.3753 19.0955C11.5301 18.9912 11.7123 18.9354 11.8989 18.9354H20C21.1046 18.9354 22 18.0399 22 16.9354V5C22 3.89543 21.1046 3 20 3Z" fill="currentColor"/>
            <rect x="6" y="12" width="7" height="2" rx="1" fill="currentColor"/>
            <rect x="6" y="7" width="12" height="2" rx="1" fill="currentColor"/>
          </svg>
        </span>
      </button>
      <div className="position-relative">
        <button
          className={`btn btn-sm btn-icon rounded-circle shadow-sm ${activeMode === 'chat' ? 'btn-primary' : 'btn-light'}`}
          type="button"
          title="Chat"
          onClick={() => onSwitch('chat')}
          style={{width: '36px', height: '36px'}}
        >
          <span className="svg-icon svg-icon-4 m-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M6.28548 15.0861C7.34369 13.1814 9.35142 12 11.5304 12H12.4696C14.6486 12 16.6563 13.1814 17.7145 15.0861L19.3493 18.0287C20.0899 19.3618 19.1259 21 17.601 21H6.39903C4.87406 21 3.91012 19.3618 4.65071 18.0287L6.28548 15.0861Z" fill="currentColor"/>
              <rect opacity="0.3" x="8" y="3" width="8" height="8" rx="4" fill="currentColor"/>
            </svg>
          </span>
        </button>
        {/* Unread badge on Chat tab */}
        {unreadTotal > 0 && activeMode !== 'chat' && (
          <span
            className="position-absolute badge badge-danger badge-circle"
            style={{top: '-4px', right: '-4px', minWidth: '16px', height: '16px', fontSize: '9px', lineHeight: '16px', padding: 0}}
          >
            {unreadTotal > 99 ? '99+' : unreadTotal}
          </span>
        )}
      </div>
    </div>
  )
}

export default FloatingDrawer;
